import { getErrorMessage, log, setLogTracking } from "@soulbyte/core";
import type { User } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import packageJson from "../../package.json";

// only built bundles (deployed dev and prd) report to Sentry; local `vite dev` never does.
// this block also wires log tracking, so log.error only reaches Sentry when this ran.
if (process.env.NODE_ENV === "production") {
  // without a dsn the sdk builds no transport at all: it swallows every event
  // silently, and the only symptom users ever see is the feedback form failing
  if (!import.meta.env.VITE_SENTRY_DSN) {
    log("sentry_dsn_missing", {
      level: "warning",
      reason:
        "this build carries no VITE_SENTRY_DSN, so nothing reaches sentry",
    });
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Disable request headers and IP for users as required by GDPR. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: false,
    integrations: [
      // If you're using react router, use the integration for your react router version instead.
      // Learn more at https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    // Disable logs since we want to be intentional about what we send to Sentry
    enableLogs: false,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing. Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
    // Capture Replay for 10% of all sessions, plus for 100% of sessions with an error. Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking - TODO: Add prerelease/environment tags to the release name for better filtering in Sentry
    release: `${packageJson.name}@${packageJson.version}`,
    // one of: local, dev, prd — must be set at build time, sentry defaults to "production" when missing
    environment: import.meta.env.VITE_SENTRY_ENV,
  });

  setLogTracking({
    captureMessage: (message, options) =>
      Sentry.captureMessage(message, options as Sentry.CaptureContext),
    captureException: (error, options) =>
      Sentry.captureException(error, options as Sentry.CaptureContext),
    getErrorMessage: (error) => getErrorMessage(error, "An error occurred"),
  });
}

/**
 * Sets user context in Sentry from Frontegg user object.
 * Call this whenever the user logs in or the user object changes.
 */
export function setSentryUser(
  user: { id?: string; email?: string; name?: string } | null,
) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  const sentryUser: User = {};
  if (user.id) sentryUser.id = user.id;
  if (user.name) sentryUser.username = user.name;
  if (user.email) sentryUser.email = user.email;

  Sentry.setUser(sentryUser);
}

/**
 * Clears user context from Sentry.
 * Call this when the user logs out.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}
